/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect } from "react";
import { toast } from "react-toastify";

export default function useResetPassword(isArabic) {
  const [tenantName, setTenantName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg,#21003D,#7400BF)",
    textLight: "#f3e9ff",
  };

  useEffect(() => {
    const val = newPassword;
    if (!val) return setPasswordStrength("");

    const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
    const medium = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;

    if (strong.test(val)) setPasswordStrength(isArabic ? "قوية" : "Strong");
    else if (medium.test(val))
      setPasswordStrength(isArabic ? "متوسطة" : "Medium");
    else setPasswordStrength(isArabic ? "ضعيفة" : "Weak");
  }, [newPassword, isArabic]);

  const getStrengthColor = () =>
    passwordStrength.match(/Strong|قوية/)
      ? "#16a34a"
      : passwordStrength.match(/Medium|متوسطة/)
      ? "#f97316"
      : passwordStrength
      ? "#dc2626"
      : "#d1d5db";

  const getStrengthValue = () =>
    passwordStrength.match(/Strong|قوية/)
      ? "100%"
      : passwordStrength.match(/Medium|متوسطة/)
      ? "60%"
      : passwordStrength
      ? "30%"
      : "0%";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantName || !email || !phone || !newPassword) {
      toast.error(isArabic ? "يرجى تعبئة جميع الحقول" : "Please fill in all fields", {
        rtl: isArabic,
        theme: "colored",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast.error(
        isArabic
          ? "كلمة المرور يجب أن تكون 6 أحرف على الأقل"
          : "Password must be at least 6 characters",
        { rtl: isArabic, theme: "colored" }
      );
      return;
    }

    setLoading(true);

    try {
      const baseUrl =
        process.env.NODE_ENV === "development"
          ? "/api"
          : (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

      const res = await fetch(`${baseUrl}/ResetPassword/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantName,
          email,
          phone,
          newPassword,
        }),
      });

      if (res.ok) {
        toast.success(
          isArabic
            ? "تم تغيير كلمة المرور بنجاح ✅"
            : "Password reset successful ✅",
          { rtl: isArabic, theme: "colored" }
        );

        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        toast.error(
          isArabic
            ? "فشل إعادة تعيين كلمة المرور ❌"
            : "Password reset failed ❌",
          { rtl: isArabic, theme: "colored" }
        );
      }
    } catch {
      toast.error(
        isArabic ? "فشل الاتصال بالخادم" : "Failed to connect to server",
        { rtl: isArabic, theme: "colored" }
      );
    } finally {
      setLoading(false);
    }
  };

  return {
    tenantName,
    setTenantName,
    email,
    setEmail,
    phone,
    setPhone,
    newPassword,
    setNewPassword,
    showPassword,
    setShowPassword,
    passwordStrength,
    getStrengthColor,
    getStrengthValue,
    loading,
    isMobile,
    COLORS,
    handleSubmit,
  };
}
